//* Libraries imports
import React from "react";
import { Container } from "@mui/material";

//* Components Imports
import ScrollTransition from "components/ScrollTransition";

//* Styles Imports
import styles from "./styles.module.css";

//* Icons Imports
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

function OrderCycleSection() {
  return (
    <section
      id="order-cycle-section"
      data-testid="order-cycle-section"
      aria-label="Seção informativa sobre o fluxo e ciclo do pedido do cliente até a entrega"
      className={styles.cycleSection}
    >
      <Container
        id="cycle-container"
        data-testid="cycle-container"
        aria-label="Container do ciclo do pedido"
        maxWidth="xl"
      >
        <ScrollTransition
          id="cycle-title-transition"
          data-testid="cycle-title-transition"
          aria-label="Animação do título do ciclo do pedido"
        >
          <h2
            id="cycle-title"
            data-testid="cycle-title"
            aria-label="Título: O Ciclo do Pedido"
            className={styles.sectionTitle}
          >
            <span className={styles.highlight}>O Ciclo do </span>Pedido
          </h2>
          <div
            id="cycle-divider"
            data-testid="cycle-divider"
            aria-label="Linha decorativa do ciclo do pedido"
            className={styles.divider}
          />
        </ScrollTransition>

        <ScrollTransition
          id="cycle-timeline-transition"
          data-testid="cycle-timeline-transition"
          aria-label="Animação do fluxo horizontal do ciclo do pedido"
          delay={200}
        >
          <div
            id="timeline-horizontal"
            data-testid="timeline-horizontal"
            aria-label="Linha do tempo horizontal mostrando as etapas de processamento do pedido"
            className={styles.timelineWrapper}
          >
            <div
              id="timeline-step-1"
              data-testid="timeline-step-1"
              aria-label="Etapa 1: Aceite do pedido pela loja parceira"
              className={styles.timelineNode}
            >
              <div
                id="timeline-icon-1"
                data-testid="timeline-icon-1"
                aria-label="Ícone de Aceite"
                className={styles.timelineIconContainer}
              >
                <CheckCircleIcon
                  id="cycle-icon-1"
                  data-testid="cycle-icon-1"
                  aria-label="Ícone de círculo com visto"
                  className={styles.timelineMuiIcon}
                />
              </div>
              <h3
                id="timeline-step-title-1"
                data-testid="timeline-step-title-1"
                aria-label="Título: Aceite"
                className={styles.timelineStepTitle}
              >
                <span className={styles.highlight}>Aceite</span>
              </h3>
              <p
                id="timeline-step-desc-1"
                data-testid="timeline-step-desc-1"
                aria-label="Descrição: A loja aceita o pedido no painel de controle ou app parceiro."
                className={styles.timelineStepDesc}
              >
                Lojista aceita ou recusa o pedido
              </p>
            </div>

            <div
              id="connector-1"
              data-testid="connector-1"
              aria-label="Linha de conexão para a próxima etapa"
              className={styles.timelineConnector}
            />

            <div
              id="timeline-step-2"
              data-testid="timeline-step-2"
              aria-label="Etapa 2: Separação física dos produtos e materiais"
              className={styles.timelineNode}
            >
              <div
                id="timeline-icon-2"
                data-testid="timeline-icon-2"
                aria-label="Ícone de Separação"
                className={styles.timelineIconContainer}
              >
                <InventoryIcon
                  id="cycle-icon-2"
                  data-testid="cycle-icon-2"
                  aria-label="Ícone de inventário"
                  className={styles.timelineMuiIcon}
                />
              </div>
              <h3
                id="timeline-step-title-2"
                data-testid="timeline-step-title-2"
                aria-label="Título: Separação"
                className={styles.timelineStepTitle}
              >
                <span className={styles.highlight}>Separação</span>
              </h3>
              <p
                id="timeline-step-desc-2"
                data-testid="timeline-step-desc-2"
                aria-label="Descrição: A equipe da loja separa e embala os materiais requisitados."
                className={styles.timelineStepDesc}
              >
                Material em separação na loja
              </p>
            </div>

            <div
              id="connector-2"
              data-testid="connector-2"
              aria-label="Linha de conexão para a próxima etapa"
              className={styles.timelineConnector}
            />

            <div
              id="timeline-step-3"
              data-testid="timeline-step-3"
              aria-label="Etapa 3: Entrega dos materiais pelo motorista"
              className={styles.timelineNode}
            >
              <div
                id="timeline-icon-3"
                data-testid="timeline-icon-3"
                aria-label="Ícone de Entrega"
                className={styles.timelineIconContainer}
              >
                <LocalShippingIcon
                  id="cycle-icon-3"
                  data-testid="cycle-icon-3"
                  aria-label="Ícone de caminhão de entrega"
                  className={styles.timelineMuiIcon}
                />
              </div>
              <h3
                id="timeline-step-title-3"
                data-testid="timeline-step-title-3"
                aria-label="Título: Entrega"
                className={styles.timelineStepTitle}
              >
                <span className={styles.highlight}>Entrega</span>
              </h3>
              <p
                id="timeline-step-desc-3"
                data-testid="timeline-step-desc-3"
                aria-label="Descrição: O motorista parceiro iGooB retira os produtos e realiza o trajeto."
                className={styles.timelineStepDesc}
              >
                Saiu para entrega ao cliente
              </p>
            </div>

            <div
              id="connector-3"
              data-testid="connector-3"
              aria-label="Linha de conexão para a etapa final"
              className={styles.timelineConnector}
            />

            <div
              id="timeline-step-4"
              data-testid="timeline-step-4"
              aria-label="Etapa 4: Conclusão e recebimento do produto pelo cliente"
              className={styles.timelineNode}
            >
              <div
                id="timeline-icon-4"
                data-testid="timeline-icon-4"
                aria-label="Ícone de Conclusão"
                className={styles.timelineIconContainer}
              >
                <TaskAltIcon
                  id="cycle-icon-4"
                  data-testid="cycle-icon-4"
                  aria-label="Ícone de tarefa concluída"
                  className={styles.timelineMuiIcon}
                />
              </div>
              <h3
                id="timeline-step-title-4"
                data-testid="timeline-step-title-4"
                aria-label="Título: Conclusão"
                className={styles.timelineStepTitle}
              >
                <span className={styles.highlight}>Conclusão</span>
              </h3>
              <p
                id="timeline-step-desc-4"
                data-testid="timeline-step-desc-4"
                aria-label="Descrição: Cliente valida a carga no canteiro e o pagamento é liberado para a loja."
                className={styles.timelineStepDesc}
              >
                Avaliação e comentário do cliente
              </p>
            </div>
          </div>
        </ScrollTransition>
      </Container>
    </section>
  );
}

export default OrderCycleSection;
